import React from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';

import { FeedbackContext, LocalizationContext } from '@cvt/contexts';
import { useRouter } from '@cvt/routing';

import { cacheKeys as authCacheKeys } from '@modules/Auth/config';
import { cacheKeys as assertionCacheKeys } from '@modules/Assertions/config';
import { cacheKeys as disciplinesCacheKeys } from '@modules/Disciplines/config';
import { cacheKeys as majorsCacheKeys } from '@modules/Majors/config';
import { cacheKeys as universitiesCacheKeys } from '@modules/Universities/config';

import { cacheKeys } from '../config';
import { userClient } from '../client/userClient';
import { UserContext } from '../contexts';

export const useUserCrud = () => {
  const router = useRouter();
  const { user } = React.useContext(UserContext);
  const { dictionary } = React.useContext(LocalizationContext);
  const { triggerFeedback, genericErrorFeedback } = React.useContext(FeedbackContext);
  const queryClient = useQueryClient();

  const createUser = useMutation({
    mutationKey: [cacheKeys.createUser],
    mutationFn: userClient.createUser,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [cacheKeys.getUsers],
      });
      router.users.edit(data.data.id).go();
      triggerFeedback({
        severity: 'success',
        message: dictionary.feedback.changesSaved,
      });
    },
    onError: () => {
      genericErrorFeedback();
    },
  });

  const editUser = useMutation({
    mutationKey: [cacheKeys.editUser],
    mutationFn: ({ id, ...data }: Users.Edit) => userClient.editUser({ id, ...data }),
    onSuccess: (data, params) => {
      queryClient.invalidateQueries({
        queryKey: [cacheKeys.getUsers],
      });
      queryClient.invalidateQueries({
        queryKey: [cacheKeys.getUser, {
          id: params.id,
        }],
      });
      if (user?.id === params.id) {
        queryClient.invalidateQueries({
          queryKey: [authCacheKeys.getMe],
        });
      }
      triggerFeedback({
        severity: 'success',
        message: dictionary.feedback.changesSaved,
      });
    },
    onError: () => {
      genericErrorFeedback();
    },
  });

  const deleteUser = useMutation({
    mutationKey: [cacheKeys.deleteUser],
    mutationFn: (id: number) => userClient.deleteUser({ id }),
    onSuccess: (data, id) => {
      queryClient.invalidateQueries({
        queryKey: [cacheKeys.getUsers],
      });
      queryClient.removeQueries({
        queryKey: [cacheKeys.getUser, {
          id,
        }],
      });
      queryClient.removeQueries({
        queryKey: [cacheKeys.getUser, {
          id: id.toString(),
        }],
      });
      triggerFeedback({
        severity: 'success',
        message: dictionary.feedback.changesSaved,
      });
    },
    onError: () => {
      genericErrorFeedback();
    },
  });

  const inviteUser = useMutation({
    mutationKey: [cacheKeys.inviteUser],
    mutationFn: userClient.inviteUser,
    onSuccess: (result, request) => {
      triggerFeedback({
        severity: 'success',
        message: dictionary.users.invite.dialog.invitationSentInfo(request.email),
      });
    },
    onError: () => {
      genericErrorFeedback();
    },
  });

  const sendSupportContact = useMutation({
    mutationKey: [cacheKeys.sendSupportContact],
    mutationFn: userClient.sendSupportContact,
    onSuccess: () => {
      triggerFeedback({
        severity: 'success',
        message: dictionary.users.supportContact.dialog.successMessage,
      });
    },
    onError: () => {
      genericErrorFeedback();
    },
  });

  const resetAccount = useMutation({
    mutationKey: [cacheKeys.resetUser],
    mutationFn: userClient.resetUser,
    onSuccess: async () => {
      triggerFeedback({
        severity: 'success',
        message: 'Your account has been reset successfully.',
      });

      await queryClient.invalidateQueries({
        queryKey: [authCacheKeys.getMe],
      });
      await queryClient.invalidateQueries({
        queryKey: [assertionCacheKeys.getNextAssertion],
      });
      await queryClient.invalidateQueries({
        queryKey: [disciplinesCacheKeys.getDisciplines],
      });
      await queryClient.invalidateQueries({
        queryKey: [majorsCacheKeys.getMajors],
      });
      await queryClient.invalidateQueries({
        queryKey: [universitiesCacheKeys.getUniversities],
      });
      router.home.go();
    },
    onError: () => {
      genericErrorFeedback();
    },
  });

  return {
    createUser: createUser.mutateAsync,
    editUser: editUser.mutateAsync,
    deleteUser: deleteUser.mutateAsync,
    inviteUser: inviteUser.mutateAsync,
    resetAccount: resetAccount,
    sendSupportContact: sendSupportContact,
  };
};
